import subMinutes from 'date-fns/subMinutes';
import addMinutes from 'date-fns/addMinutes';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

export const DEFAULT_TIMEZONE = 'Europe/Paris';

const FRENCH_LOCALE_LANGUAGE = 'fr';
const FRENCH_LOCALE_COUNTRY = 'FR';
const FRENCH_LOCALE = `${FRENCH_LOCALE_LANGUAGE}-${FRENCH_LOCALE_COUNTRY}`;

/**
 * Loads dynamivally a locale
 * @param locale the locale to load
 * @returns the loaded locale
 */
export const getLocale = (locale: string): Promise<Locale> => require(`date-fns/locale/${locale}/index.js`);

/**
 * Formats a date to custom format
 * @param date the date to format
 * @param formatStyle the format style (see date fns doc)
 * @param locale the locale to use (default to fr-FR)
 * @returns the formatted date
 */
export const formatDate = (date: Date, formatStyle: string, localeName = FRENCH_LOCALE_LANGUAGE): string => {
  if (!localeName) return '';

  const locale: Locale = getLocale(localeName) as Locale;
  const options = { locale };

  return format(date, formatStyle, options);
};

/**
 * Formats a calendar date string ("2026-07-17") to custom format.
 * Parsed with parseISO (local midnight), never new Date (UTC midnight),
 * so the displayed day is timezone-independent.
 */
export const formatCalendarDate = (calendarDateString: string, formatStyle: string, localeName = FRENCH_LOCALE_LANGUAGE): string =>
  formatDate(parseISO(calendarDateString), formatStyle, localeName);

/**
 * Converts a time to date.
 * @param time the time to format.
 * @returns the date.
 */
const convertLocalTimeToDate = (time: string): Date => {
  if (!time) return null;

  const day = new Date();
  const splitTime = time.split(/:/);

  day.setHours(parseInt(splitTime[0]));
  day.setMinutes(parseInt(splitTime[1]));
  splitTime[2] && day.setSeconds(parseInt(splitTime[2]));

  return day;
};

/**
 * Converts a date to time
 * @param dateIsoString the date iso string.
 * @returns the time.
 */
const convertDateToLocalTime = (dateIsoString: string | Date) => {
  if (!dateIsoString) return '';

  const date = new Date(dateIsoString);
  const localTime = date.toLocaleTimeString(FRENCH_LOCALE, { hour: '2-digit', minute: '2-digit' });

  return localTime;
};

/**
 * Get a time value in milliseconds, in user's browser timezone.
 * @param time the time to format in local timezone.
 * @returns the time formatted.
 */
export const convertLocalTimeToMillisecondsTime = (time: string): number => {
  if (!time) return null;

  return convertLocalTimeToDate(time).getTime();
};

/**
 * Format a date to "Le 24/02/2021 10:00" format for display, in user's browser timezone.
 * @param dateString the date to format.
 * @returns the time formatted.
 */
export const formatDateWithTime = (dateIsoString: string | Date): string => {
  if (!dateIsoString) return '';

  const date = new Date(dateIsoString);
  const localTime = convertDateToLocalTime(dateIsoString);
  const splitTime = localTime.split(':');

  return `Le ${date.toLocaleDateString()} à ${splitTime[0]}:${splitTime[1]}`;
};

/**
 * Format a time to "10:00" format for display, in user's browser timezone
 * @param dateIsoString the date to format
 * @returns the time formatted
 */
export const formatTimeFromDate = (dateIsoString: string | Date): string => {
  if (!dateIsoString) return '';

  const localTime = convertDateToLocalTime(dateIsoString);
  const splitTime = localTime.split(':');

  return `${splitTime[0]}:${splitTime[1]}`;
};

/**
 * Format a time from "10:00:00" to "10:00" for display
 * @param time the time to format
 * @returns the time formatted
 */
export const formatTime = (time: string): string => {
  if (!time) return '';

  const splitTime = time.split(':');

  return `${splitTime[0]}:${splitTime[1]}`;
};

/**
 * Substract minutes to time in format "10:00:00"
 */
export const subMinutesToTime = (time: string, hours: number): string => {
  if (!time) return '';

  return formatTimeFromDate(subMinutes(convertLocalTimeToDate(time), hours));
};

/**
 * Add minutes to time in format "10:00:00"
 */
export const addMinutesToTime = (time: string, hours: number): string => {
  if (!time) return '';

  return formatTimeFromDate(addMinutes(convertLocalTimeToDate(time), hours));
};

/**
 *  Format a day of week from 1 to lun. for display
 * @param dayOfWeek the day of week to format (1,2,3,4,5,6,7)
 * @returns the day of week formatted (lun., mar., mer., jeu., ven., sam. dim.)
 */
export const formatDayOfWeek = (dayOfWeek: number, localeName = FRENCH_LOCALE_LANGUAGE) => {
  const locale: Locale = getLocale(localeName) as Locale;
  const date = new Date();
  date.setDate(date.getDate() + ((dayOfWeek + 7 - date.getDay()) % 7));
  const formattedDay = format(date, 'E', { locale });

  return formattedDay;
};
